import SEO from 'global/SEO';
import GuestStream from 'global/StreamWidget/GuestStream';
import { Fragment, useState } from 'react';
import Link from 'next/link';
import { assetPrefix } from 'constants/app';
import { CompanyChartData } from 'lib/entities/company.types';
import GuestHeader from './GuestHeader';
import {
  Banner,
  CustomGuestCompanyBackground,
  GuestChartWrapper,
  GuestNavbar,
  GuestStreamWrapper,
} from './styled';
import { StreamPostData } from '../../../@types/stream';
import GuestRegisterModal from './GuestRegisterModal';
import { ITabItems } from '../../../@types/company.d';
import { TAB_ANALYST_NEW_NAME, TAB_LIST, TAB_LIST_NAME } from './constants';
import ChartV2 from './Chart';

interface GuestCompanyProps {
  symbol: string;
  posts: StreamPostData[];
  company: any;
  isCompanyStock?: boolean;
  companyProfile?: any;
  chart?: CompanyChartData;
}

const features = [
  {
    name: 'Bandar Detector',
    icon: 'icon-feature_bandar',
  },
  {
    name: 'Foreign Flow Chart',
    icon: 'icon-feature_foreign',
  },
  { name: 'Valuation Tool', icon: 'icon-feature_valuation' },
  { name: 'Stock Screener', icon: 'icon-feature_screener' },
  { name: 'Price-Alert', icon: 'icon-feature_pricealert' },
  { name: 'Real-time Charting Platform', icon: 'icon-feature_charting' },
];

const CompanyGuestStream = ({
  symbol,
  posts,
  company,
  isCompanyStock,
  companyProfile,
  chart,
}: GuestCompanyProps) => {
  const DEFAULT_VISIBLE_STATE = !isCompanyStock;
  const [visible, setVisible] = useState(DEFAULT_VISIBLE_STATE);
  const { tabs = [], name } = company;
  const metaTitle = isCompanyStock
    ? `Saham: ${symbol.toUpperCase()} - PT. ${name} | Stockbit`
    : `Saham ${symbol.toUpperCase()} (${name}) | Stockbit`;
  const metaDescription = isCompanyStock
    ? `Informasi detail tentang saham ${symbol.toUpperCase()} - PT. ${name} hari ini, mulai dari harga, grafik, data historis dan profil lengkap lainnya.`
    : `Lihat berita, harga saham, data fundamental, teknikal analisis dan laporan keuangan saham ${symbol.toUpperCase()} (${name}) Investasi, Nabung, Beli atau Jual sahamnya secara online dengan mudah.`;

  const generateSchemaJson = () => {
    const { name: companyName, exchange } = company;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://stockbit.com/symbol/${symbol}`,
      },
      headline: `Saham ${symbol} - PT. ${companyName} | Stockbit`,
      image: '',
      author: {
        '@type': 'Organization',
        name: 'Stockbit',
        url: 'https://stockbit.com',
      },
      mentions: [
        {
          '@type': 'Corporation',
          Legalname: `PT. ${companyName}`,
          tickerSymbol: `${exchange}:${symbol}`,
          url: `https://stockbit.com/symbol/${symbol}`,
        },
      ],
      description: `Informasi detail tentang saham ${symbol} - PT. ${companyName} hari ini, mulai dari harga, grafik, data historis dan profil lengkap lainnya.`,
    };
  };

  const customSymbolMetaKeywords = {
    PANI: 'saham pantai indah kapuk, pik 2, saham pani hari ini, pani stock split, aguan, agung sedayu group, saham konglo, saham konglomerat indonesia, analisa saham pani, pemegang saham pani, saham properti, saham sektor properti, emiten properti, perumahan pik',
    PTRO: 'saham petrosea, saham prajogo pangestu, saham ptro hari ini, analisa saham ptro, berita saham ptro, berita saham ptro, saham ptro tbk, siapa pemilik petrosea, saham prajogo pangestu apa saja, perusahaan prajogo pangestu, sektor saham energi, perusahaan gas, saham batu bara, industri minyak, jasa konstruksi',
    BREN: 'saham BREN hari ini, pemegang saham bren, harga saham bren, dividen bren 2024, analisa saham bren besok, analisa saham bren hari ini, saham bergerak dibidang apa, saham bren milik siapa, prajogo pangestu borong saham bren, laba bren 2024, lapkeu bren 2024, grup barito pacific, sektor energi terbarukan',
    MLPL: 'saham MLPL price, dividen saham mlpl 2024, saham mlpl tbk, saham mlpl review, harga wajar saham mlpl, saham mlpl bergerak dibidang apa, multipolar lippo',
  };

  const generateMetaKeywords = () => {
    const symbolKeywords = `${symbol}, saham ${symbol}, ${symbol} saham, idx ${symbol}, ${symbol} idx, harga saham ${symbol}, harga saham ${symbol} hari ini, saham ${symbol} hari ini, ${symbol} saham hari ini, laporan keuangan ${symbol}, grafik saham ${symbol}, dividen ${symbol}, ${symbol} stock, ${symbol} hari ini, ${symbol} saham apa, analisa saham ${symbol}, saham ${symbol} bergerak di bidang apa, berita saham ${symbol}, saham ${symbol} chart, pemegang saham ${symbol}, analisis saham ${symbol}`;

    const companyKeywords = `saham ${name}, harga saham ${name}, harga saham ${name} hari ini, ${name} saham, harga 1 lot saham ${name}, grafik saham ${name}, cara beli saham ${name}, beli saham ${name}, kode saham ${name}, saham ${name} bergerak di bidang, investasi saham ${name}`;

    if (customSymbolMetaKeywords[symbol?.toUpperCase()] !== undefined) {
      return `${
        customSymbolMetaKeywords[symbol?.toUpperCase()]
      }, ${symbolKeywords}, ${companyKeywords}`;
    }

    return `${symbolKeywords}, ${companyKeywords}`;
  };

  const guestCompanyTabs: Array<ITabItems> = tabs
    .filter((value) => value !== TAB_LIST_NAME.NEWS)
    .map((value) => {
      let newValue = value;
      if (value === TAB_ANALYST_NEW_NAME) {
        newValue = TAB_LIST_NAME.ANALYST;
      }

      return TAB_LIST[newValue];
    });

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        keywords={isCompanyStock ? generateMetaKeywords() : undefined}
        schemaJson={isCompanyStock ? generateSchemaJson() : null}
      />

      <div style={{ width: '100%' }}>
        <GuestHeader
          symbol={symbol}
          company={company}
          onClickLink={() => setVisible(true)}
        />
        {isCompanyStock && (
          <>
            <GuestChartWrapper>
              <ChartV2
                symbol={symbol}
                company={company}
                chart={chart}
                onSelectTimeFrame={() => setVisible(true)}
                onExpand={() => setVisible(true)}
                onClickChart={() => setVisible(true)}
                isChartVisible
                timeframe="today"
                disableChartbitLink
              />
            </GuestChartWrapper>
            <CustomGuestCompanyBackground
              companyProfile={companyProfile}
              onClickReadMore={setVisible}
            />
          </>
        )}
        <GuestNavbar
          tabs={guestCompanyTabs}
          active={TAB_LIST_NAME.STREAM}
          isCompanyStock={isCompanyStock}
          onClickTab={() => setVisible(true)}
        />
        <GuestStreamWrapper
          // @ts-expect-error
          isCompanyStock={isCompanyStock}
          justify="space-between"
        >
          <GuestStream posts={posts} isCompanyStock={isCompanyStock} />
          {!isCompanyStock && (
            <Banner>
              <Link href="/register">
                <a>
                  <img
                    src={`${assetPrefix}/images/sekuritas-banner-right.svg`}
                    alt="Daftar"
                  />
                </a>
              </Link>
            </Banner>
          )}
        </GuestStreamWrapper>
      </div>
      <GuestRegisterModal
        isCompanyStock={isCompanyStock}
        visible={visible}
        setVisible={setVisible}
        features={features}
      />
    </>
  );
};

export default CompanyGuestStream;
